import React, { useState } from 'react';
import { Paper, Typography, TextField, Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { api } from '../../services/api';
import { isPending } from '../../services/apiUtils.js';

function SurveyContextEditor({ userData, setUserData, setWarningMessage, setOpenWarningSnackbar, onTopicUpdate, readOnly }) {
  const [surveyContext, setSurveyContext] = useState(userData.surveyContext || '');
  const [isEditing, setIsEditing] = useState(!userData.surveyContext && !readOnly);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (readOnly) return;
    
    if (!userData.userID || !userData.researchID) {
      setError('Missing user or research ID');
      return;
    }

    if (isPending(userData, setWarningMessage, setOpenWarningSnackbar)) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      const result = await api.addContext(userData.userID, userData.researchID, surveyContext);
      const newUserData = { ...userData, surveyContext, responsesStatus: result.responsesStatus, lastModified: Math.floor(Date.now() / 1000) };
      setUserData(newUserData);
      setIsEditing(false);
      onTopicUpdate(newUserData);
    } catch (e) {
      console.error('Error saving survey context:', e);
      setError('Failed to save survey context');
    } finally {
      setSaving(false);
    }
  };

  // If in readOnly mode and there is no context, don't render anything
  if (readOnly && !userData.surveyContext) {
    return null;
  }

  return (
    
    <Paper sx={{ p: 3, mt: 2, mb: 2 }}>
      <Tooltip 
                title="This can include background information, facts that should apply to all personas, or any other relevant details that will help the personas understand the survey better."
                placement="top"
                arrow
              >
        <Typography variant="h6" gutterBottom>
            {readOnly ? 'Persona Context' : 'Provide the personas with extra context on your survey (optional)'}
          </Typography>
      </Tooltip>
      
      {isEditing && !readOnly ? (
            <>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={surveyContext}
                onChange={(e) => {
                  setSurveyContext(e.target.value);
                  setError('');
                }}
                placeholder="Our survey is for a fitness app targeting young professionals. Respondents should be interested in home workouts and nutrition tracking. We want to understand their exercise habits and pain points with existing fitness solutions."
                variant="outlined"
                sx={{ mt: 1, mb: 2, '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
              />
      
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              size="small"
            >
              {saving ? <CircularProgress size={16} /> : 'Save context'}
            </Button>
            {error && (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            )}
          </Box>
        </>
      ) : (
        <>
          <Typography sx={{ whiteSpace: 'pre-line', mb: 2, color: 'text.secondary' }}>
            {surveyContext || (readOnly ? "No context provided." : "")}
          </Typography>
          
          {!readOnly && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsEditing(true)}
          >
            Edit context
          </Button>
          )}
        </>
      )}
    </Paper>
  );
}

export default SurveyContextEditor; 